import { useCallback, useEffect, useState } from 'react'
import { api } from '../servizi/api.ts'
import {
  AreaTesto, Bottone, Campo, Etichetta, Modale, Paginazione, Riquadro, Scelta, Tabella,
  type TonoEtichetta,
} from '../componenti/base.tsx'
import { LIMITE, conteggio, giorno, messaggioErrore, usaElenco } from '../componenti/elenco.tsx'
import { Attesa, Avviso, Dato, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'

/**
 * Registro delle decisioni funzionali (ALT-28).
 *
 * Elenco filtrabile delle decisioni che hanno orientato il gestionale, con il dettaglio della
 * fonte e i comandi di approvazione per la referente funzionale dell'art. 7. L'approvazione non
 * blocca lo sviluppo: la pagina mostra da quanti giorni ciascuna decisione attende, perche' e'
 * quel numero che il piano di collaudo dovra' riportare (V-13).
 */

type Decisione = {
  id: string
  codice: string
  titolo: string
  domanda: string
  scelta: string
  motivazione: string | null
  requisiti: string[]
  fonte: string
  riferimento_fonte: string | null
  decisa_da_nome: string
  decisa_il: string
  stato: string
  approvata_da_nome: string | null
  approvata_il: string | null
  nota_approvazione: string | null
  impatto_contratto: boolean
  giorni_attesa: number | null
  superata_da_codice: string | null
}

type Conteggio = {
  totale: number
  in_attesa: number
  attesa_massima_giorni: number | null
  puo_approvare: boolean
}

const STATI: Record<string, { etichetta: string; tono: TonoEtichetta }> = {
  proposta: { etichetta: 'proposta', tono: 'informativo' },
  in_approvazione: { etichetta: 'in attesa', tono: 'attenzione' },
  approvata: { etichetta: 'approvata', tono: 'positivo' },
  respinta: { etichetta: 'respinta', tono: 'critico' },
  superata: { etichetta: 'superata', tono: 'neutro' },
}

const FONTI: Record<string, string> = {
  risposte: 'questionario', chat: 'chat', email: 'email', pec: 'PEC',
  gestionale: 'gestionale', riunione: 'riunione',
}

/** Parametri di ricerca, senza le chiavi vuote: il percorso e' anche la chiave della lettura. */
function percorsoElenco(filtri: { stato: string; requisito: string; testo: string }) {
  const p = new URLSearchParams()
  if (filtri.stato) p.set('stato', filtri.stato)
  if (filtri.requisito) p.set('requisito', filtri.requisito)
  if (filtri.testo.trim()) p.set('testo', filtri.testo.trim())
  const query = p.toString()
  return query ? `/decisioni?${query}` : '/decisioni'
}

export function Decisioni() {
  const [stato, setStato] = useState('')
  const [requisito, setRequisito] = useState('')
  const [testo, setTesto] = useState('')
  const [scritto, setScritto] = useState('')
  const { dati, errore, ricarica, finestra } =
    usaElenco<Decisione>(percorsoElenco({ stato, requisito, testo }))
  const [aperta, setAperta] = useState<Decisione | null>(null)
  const [riepilogo, setRiepilogo] = useState<Conteggio | null>(null)

  const leggiRiepilogo = useCallback(() => {
    api.get<Conteggio>('/decisioni/conteggio').then(setRiepilogo).catch(() => setRiepilogo(null))
  }, [])
  useEffect(leggiRiepilogo, [leggiRiepilogo])

  function aggiorna() { ricarica(); leggiRiepilogo() }

  return (
    <>
      <Intestazione
        titolo="Decisioni"
        sottotitolo={conteggio(finestra.totale, 'decisione', 'decisioni')}
        azioni={
          <a className="bottone bottone-secondario" href="/api/decisioni/esporta">
            Esporta in markdown
          </a>
        }
      />

      {/* Il numero in attesa sta sopra l'elenco: e' il dato che il piano di collaudo dovra'
          riportare, e chi apre la pagina lo deve vedere senza cercarlo. */}
      {riepilogo && riepilogo.in_attesa > 0 && (
        <Avviso tipo="attenzione">
          {riepilogo.in_attesa} decisioni attendono l’approvazione
          {riepilogo.attesa_massima_giorni !== null
            && `, la più vecchia da ${riepilogo.attesa_massima_giorni} giorni`}
          . Lo sviluppo procede: il piano di collaudo elencherà le decisioni non approvate e i
          giorni di attesa.
        </Avviso>
      )}

      <Riquadro titolo="Filtri" azioni={(stato || requisito || testo)
        ? <Bottone variante="discreto" dimensione="piccolo"
                   onClick={() => { setStato(''); setRequisito(''); setScritto(''); setTesto('') }}>
            Azzera i filtri
          </Bottone>
        : undefined}>
        <form className="filtri-report" onSubmit={(e) => { e.preventDefault(); setTesto(scritto) }}>
          <Scelta etichetta="Stato" valore={stato} cambia={setStato} vuota="Tutti"
                  opzioni={Object.entries(STATI).map(([valore, s]) => ({
                    valore, etichetta: s.etichetta,
                  }))} />
          <Campo etichetta="Requisito" valore={requisito} cambia={setRequisito}
                 segnaposto="ALT-28" maxLength={60} />
          {/* La ricerca parte a comando, non a ogni carattere: il modulo la invia con Invio. */}
          <Campo etichetta="Testo o codice" tipo="search" valore={scritto} cambia={setScritto}
                 segnaposto="parole del titolo, della domanda o della scelta" maxLength={200} />
          <div className="atti-comando-filtro">
            <Bottone tipo="submit" variante="primario">Cerca</Bottone>
          </div>
        </form>
      </Riquadro>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !dati && <Attesa />}
      {dati && (
        <Riquadro denso>
          <Tabella intestazioni={['Codice', 'Decisione', 'Fonte', 'Stato', 'Attesa']}
                   vuoto={<Vuoto testo="Nessuna decisione con questi filtri." />}>
            {dati.map((d) => (
              <tr key={d.id}>
                <td data-etichetta="Codice">
                  <button type="button" className="atti-codice" onClick={() => setAperta(d)}>
                    {d.codice}
                  </button>
                </td>
                <td data-etichetta="Decisione">
                  <div>{d.titolo}</div>
                  {d.impatto_contratto && <Etichetta tono="critico">impatto sul contratto</Etichetta>}
                </td>
                <td data-etichetta="Fonte">
                  <div>{FONTI[d.fonte] ?? d.fonte}</div>
                  <div className="piccolo tenue">{giorno(d.decisa_il)}</div>
                </td>
                <td data-etichetta="Stato">
                  <Etichetta tono={STATI[d.stato]?.tono ?? 'neutro'}>
                    {STATI[d.stato]?.etichetta ?? d.stato}
                  </Etichetta>
                  {d.superata_da_codice && (
                    <div className="piccolo tenue">da {d.superata_da_codice}</div>
                  )}
                </td>
                <td data-etichetta="Attesa">
                  {d.stato === 'in_approvazione' || d.stato === 'proposta'
                    ? `${d.giorni_attesa ?? 0} g`
                    : '—'}
                </td>
              </tr>
            ))}
          </Tabella>
          <Paginazione salta={finestra.salta} limite={LIMITE} totale={finestra.totale}
                       ricevute={dati.length} cambia={finestra.cambia} />
        </Riquadro>
      )}

      {aperta && (
        <Dettaglio decisione={aperta} chiudi={() => setAperta(null)}
                   puoApprovare={riepilogo?.puo_approvare ?? false}
                   decisa={() => { setAperta(null); aggiorna() }} />
      )}
    </>
  )
}

function Dettaglio({ decisione, chiudi, puoApprovare, decisa }: {
  decisione: Decisione
  chiudi: () => void
  puoApprovare: boolean
  decisa: () => void
}) {
  const [nota, setNota] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const daDecidere = decisione.stato === 'in_approvazione' || decisione.stato === 'proposta'

  async function decidi(azione: 'approva' | 'respingi') {
    if (azione === 'respingi' && nota.trim().length < 3) {
      setErrore('Per respingere una decisione serve il motivo.')
      return
    }
    setInCorso(true)
    setErrore(null)
    try {
      await api.post(`/decisioni/${decisione.id}/${azione}`, { nota: nota.trim() || null })
      decisa()
    } catch (e) {
      setErrore(messaggioErrore(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo={`${decisione.codice} — ${decisione.titolo}`} chiudi={chiudi} larga
            azioni={puoApprovare && daDecidere ? (
              <>
                <Bottone onClick={chiudi}>Chiudi</Bottone>
                <Bottone variante="critico" inCorso={inCorso} onClick={() => void decidi('respingi')}>
                  Respingi
                </Bottone>
                <Bottone variante="primario" inCorso={inCorso} onClick={() => void decidi('approva')}>
                  Approva
                </Bottone>
              </>
            ) : <Bottone onClick={chiudi}>Chiudi</Bottone>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Dato nome="Domanda">{decisione.domanda}</Dato>
      <Dato nome="Scelta">{decisione.scelta}</Dato>
      {decisione.motivazione && <Dato nome="Motivazione">{decisione.motivazione}</Dato>}
      <Dato nome="Fonte">
        {FONTI[decisione.fonte] ?? decisione.fonte}
        {decisione.riferimento_fonte && ` — ${decisione.riferimento_fonte}`}
      </Dato>
      <Dato nome="Decisa da">{decisione.decisa_da_nome}, il {giorno(decisione.decisa_il)}</Dato>
      {decisione.requisiti.length > 0 && (
        <Dato nome="Requisiti">{decisione.requisiti.join(', ')}</Dato>
      )}
      <Dato nome="Stato">
        <Etichetta tono={STATI[decisione.stato]?.tono ?? 'neutro'}>
          {STATI[decisione.stato]?.etichetta ?? decisione.stato}
        </Etichetta>
        {decisione.approvata_da_nome
          && ` — ${decisione.approvata_da_nome}, il ${giorno(decisione.approvata_il)}`}
        {decisione.superata_da_codice && ` — sostituita da ${decisione.superata_da_codice}`}
      </Dato>
      {decisione.nota_approvazione && (
        <Dato nome="Nota">{decisione.nota_approvazione}</Dato>
      )}
      {decisione.impatto_contratto && (
        <Avviso tipo="attenzione">
          La decisione incide sull’oggetto del contratto: l’approvazione della referente vale
          come accettazione della variazione.
        </Avviso>
      )}
      {puoApprovare && daDecidere && (
        <AreaTesto etichetta="Nota di approvazione o motivo del rifiuto" valore={nota}
                   cambia={setNota} righe={3} maxLength={2000}
                   aiuto="Obbligatoria per respingere." />
      )}
    </Modale>
  )
}
